import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [activeSection, setActiveSection] = useState('hero');
  const sectionsRef = useRef([]);

  // Section navigation highlight on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 300;
      
      sectionsRef.current.forEach((section) => {
        if (!section) return;
        
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(section.id);
        }
      });
    };
    
    // Collect all section elements
    sectionsRef.current = Array.from(document.querySelectorAll('section[id]'));
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Handle tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="logo">
          <h2>Advantis AI</h2>
        </div>
        <div className="nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#use-cases">Use Cases</a>
          <a href="#testimonials">Testimonials</a>
          <Link to="/login" className="login-btn">Login</Link>
          <Link to="/signup" className="signup-btn">Start Building Now</Link>
        </div>
        <div className="mobile-menu-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </nav>
      
      {/* Section Navigation */}
      <nav className="section-nav">
        <ul>
          <li><a href="#hero" className={activeSection === 'hero' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">Home</span></a></li>
          <li><a href="#features" className={activeSection === 'features' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">Features</span></a></li>
          <li><a href="#how-it-works" className={activeSection === 'how-it-works' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">How It Works</span></a></li>
          <li><a href="#use-cases" className={activeSection === 'use-cases' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">Use Cases</span></a></li>
          <li><a href="#technical" className={activeSection === 'technical' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">Technical</span></a></li>
          <li><a href="#testimonials" className={activeSection === 'testimonials' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">Testimonials</span></a></li>
          <li><a href="#for-business" className={activeSection === 'for-business' ? 'active' : ''}><span className="nav-dot"></span><span className="nav-label">Enterprise</span></a></li>
        </ul>
      </nav>
      
      {/* Hero Section */}
      <section className="hero" id="hero">
        <div className="particle-animation">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        <div className="hero-content">
          <h1>Build Your Own <span className="highlight">AI Assistant</span><br />in Minutes</h1>
          <p className="subtitle">
            Upload a ton of files—docs, notes, even audio—and create a custom AI that knows you like a friend. No coding, just you and your ideas.
          </p>
          <div className="cta-buttons">
            <Link to="/signup" className="primary-btn">Start Building Now</Link>
            <a href="#features" className="secondary-btn">Learn More</a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="files-animation">
            <div className="file file-doc">
              <div className="file-icon">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2Z" />
                  <path d="M14,3V7H18" fill="rgba(255,255,255,0.3)" />
                  <path d="M7,13H17V14H7V13M7,9H17V10H7V9M7,17H14V18H7V17Z" fill="rgba(255,255,255,0.7)" />
                </svg>
              </div>
              <span>Lesson_Plan.docx</span>
            </div>
            <div className="file file-image">
              <div className="file-icon">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19,19H5V5H19M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3Z" />
                  <path d="M14.58,17L18,14V17H14.58M10.05,17L6,13.87L8,12L10.05,14.12L14.54,10L17,11.97L10.05,17Z" fill="rgba(255,255,255,0.7)" />
                </svg>
              </div>
              <span>Math_Slides.jpg</span>
            </div>
            <div className="file file-audio">
              <div className="file-icon">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.85 14,18.71V20.77C18.01,19.86 21,16.28 21,12C21,7.72 18.01,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z" />
                </svg>
              </div>
              <span>Essay_Brainstorm.m4a</span>
            </div>
            <div className="file file-note">
              <div className="file-icon">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14,10H19.5L14,4.5V10M5,3H15L21,9V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3M5,12V14H19V12H5M5,16V18H14V16H5Z" />
                </svg>
              </div>
              <span>Meeting_Notes.txt</span>
            </div>
            <div className="virtual-assistant">
              <div className="assistant-bubble">
                <div className="assistant-avatar"></div>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="section-header">
          <h2>Why You'll Love <span className="highlight">Advantis AI</span></h2>
          <p className="section-subtitle">A smarter way to build AI assistants that actually understand your world</p>
        </div>
        
        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M21,11C21,16.55 17.16,21.74 12,23C6.84,21.74 3,16.55 3,11V5L12,1L21,5V11M12,21C15.75,20 19,15.54 19,11.22V6.3L12,3.18L5,6.3V11.22C5,15.54 8.25,20 12,21M12,6A2,2 0 0,1 14,8C14,9.33 12.67,10 12,10.5C11.33,11 10,12.67 10,14A2,2 0 0,0 12,16A2,2 0 0,0 14,14H16A4,4 0 0,1 12,18A4,4 0 0,1 8,14C8,11.33 9.33,9 12,8.5C12.67,8 14,6.33 14,5A1,1 0 0,0 13,4H11A1,1 0 0,0 10,5H8A3,3 0 0,1 11,2H13A3,3 0 0,1 16,5C16,7.67 14.67,10 12,10.5V10.5Z" />
              </svg>
            </div>
            <h3>Your Personal AI Sidekick</h3>
            <p>Shape your AI's personality and fill it with knowledge from your files—great for personal tasks or small business needs.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,9A1.5,1.5 0 0,1 19,10.5A1.5,1.5 0 0,1 17.5,12M14.5,8A1.5,1.5 0 0,1 13,6.5A1.5,1.5 0 0,1 14.5,5A1.5,1.5 0 0,1 16,6.5A1.5,1.5 0 0,1 14.5,8M9.5,8A1.5,1.5 0 0,1 8,6.5A1.5,1.5 0 0,1 9.5,5A1.5,1.5 0 0,1 11,6.5A1.5,1.5 0 0,1 9.5,8M6.5,12A1.5,1.5 0 0,1 5,10.5A1.5,1.5 0 0,1 6.5,9A1.5,1.5 0 0,1 8,10.5A1.5,1.5 0 0,1 6.5,12M12,3A9,9 0 0,0 3,12A9,9 0 0,0 12,21A9,9 0 0,0 21,12A9,9 0 0,0 12,3M12,19A7,7 0 0,1 5,12A7,7 0 0,1 12,5A7,7 0 0,1 19,12A7,7 0 0,1 12,19Z" />
              </svg>
            </div>
            <h3>So Easy to Use</h3>
            <p>No tech know-how needed. Upload tons of files and get going with a few clicks.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M13,9H18.5L13,3.5V9M6,2H14L20,8V19A2,2 0 0,1 18,21H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M6,20H15V18H6V20M6,17H15V15H6V17M6,14H15V12H6V14M6,9H11V4H6V9Z" />
              </svg>
            </div>
            <h3>Pack It Full</h3>
            <p>Add a ton of documents, notes, and audio recordings—your AI learns from everything you share.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z" />
              </svg>
            </div>
            <h3>Gets Better With You</h3>
            <p>The more you upload, the smarter it gets—ready to help with answers, tasks, or inspiration.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="section-header">
          <h2>Three Simple Steps to Your <span className="highlight">AI</span></h2>
          <p className="section-subtitle">Creating your custom AI assistant has never been easier</p>
        </div>
        
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
              </svg>
            </div>
            <div className="step-content">
              <h3>Upload Your Stuff</h3>
              <p>Drop in documents, notes, or audio files—give your AI a big head start with all you've got.</p>
            </div>
          </div>
          
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,4C14.21,4 16,5.79 16,8C16,10.21 14.21,12 12,12C9.79,12 8,10.21 8,8C8,5.79 9.79,4 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
              </svg>
            </div>
            <div className="step-content">
              <h3>Make It Your Own</h3>
              <p>Adjust its tone and focus to match what you need—quick and painless.</p>
            </div>
          </div>
          
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,10.5A1.5,1.5 0 0,1 13.5,12A1.5,1.5 0 0,1 12,13.5A1.5,1.5 0 0,1 10.5,12A1.5,1.5 0 0,1 12,10.5M7.5,10.5A1.5,1.5 0 0,1 9,12A1.5,1.5 0 0,1 7.5,13.5A1.5,1.5 0 0,1 6,12A1.5,1.5 0 0,1 7.5,10.5M16.5,10.5A1.5,1.5 0 0,1 18,12A1.5,1.5 0 0,1 16.5,13.5A1.5,1.5 0 0,1 15,12A1.5,1.5 0 0,1 16.5,10.5Z" />
              </svg>
            </div>
            <div className="step-content">
              <h3>Put It to Work</h3>
              <p>Ask questions, organize your day, or brainstorm—your AI's ready to roll.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="use-cases">
        <div className="section-header">
          <h2>How It Fits <span className="highlight">Your Life</span></h2>
          <p className="section-subtitle">Real solutions for everyday people and small businesses</p>
        </div>
        
        <div className="use-cases-tabs">
          <div className="tabs-nav">
            <button 
              className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`} 
              onClick={() => handleTabChange('personal')}
            >
              Personal
            </button>
            <button 
              className={`tab-btn ${activeTab === 'business' ? 'active' : ''}`} 
              onClick={() => handleTabChange('business')}
            >
              Business
            </button>
            <button 
              className={`tab-btn ${activeTab === 'education' ? 'active' : ''}`} 
              onClick={() => handleTabChange('education')}
            >
              Education
            </button>
            <button 
              className={`tab-btn ${activeTab === 'health' ? 'active' : ''}`} 
              onClick={() => handleTabChange('health')}
            >
              Health & Fitness
            </button>
          </div>
          
          <div className="tabs-content">
            <div className={`tab-panel ${activeTab === 'personal' ? 'active' : ''}`}>
              <div className="use-cases-grid">
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                    </svg>
                  </div>
                  <h3>Your Everyday Helper</h3>
                  <p>Upload your schedules, to-do lists, or voice memos—stay on top of things with a trusty sidekick.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"When's my dentist appointment again?"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9M10,16V19.08L13.08,16H20V4H4V16H10Z" />
                    </svg>
                  </div>
                  <h3>Idea Booster</h3>
                  <p>Add your written notes, journal entries, or audio thoughts—turn them into fresh ideas with your AI.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"What patterns do you see in my brainstorming?"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20,14H4V8H7V10H9V8H15V10H17V8H20V14M20,19H4V17H20V19M9,4H15V6H9V4M20,6H17V4C17,2.89 16.11,2 15,2H9C7.89,2 7,2.89 7,4V6H4C2.89,6 2,6.89 2,8V19C2,20.11 2.89,21 4,21H20C21.11,21 22,20.11 22,19V8C22,6.89 21.11,6 20,6Z" />
                    </svg>
                  </div>
                  <h3>Travel Companion</h3>
                  <p>Upload travel itineraries, local guides, and personal preferences—have a travel advisor that knows exactly what you like.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"What activities in X match my interests?"</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`tab-panel ${activeTab === 'business' ? 'active' : ''}`}>
              <div className="use-cases-grid">
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16,15H9V13H16M19,11H9V9H19M19,7H9V5H19M21,1H7C5.89,1 5,1.89 5,3V17C5,18.11 5.9,19 7,19H21C22.11,19 23,18.11 23,17V3C23,1.89 22.1,1 21,1M3,5V21H19V23H3A2,2 0 0,1 1,21V5H3Z" />
                    </svg>
                  </div>
                  <h3>Small Business Buddy</h3>
                  <p>Add financial reports, meeting notes, and market research—gain deeper insights about your own business operations.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"What trends are showing in my quarterly sales data?"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12,15C7.58,15 4,16.79 4,19V21H20V19C20,16.79 16.42,15 12,15M8,9A4,4 0 0,0 12,13A4,4 0 0,0 16,9M11.5,2C11.2,2 11,2.21 11,2.5V5.5H10V3C10,2.45 10.45,2 11,2H12C12.55,2 13,2.45 13,3V5.5H12V2.5C12,2.21 11.8,2 11.5,2M9.5,2C9.2,2 9,2.21 9,2.5V5.5H8V3C8,2.45 8.45,2 9,2H10C10.55,2 11,2.45 11,3V5.5H10V2.5C10,2.21 9.8,2 9.5,2M7.5,2C7.2,2 7,2.21 7,2.5V5.5H6V3C6,2.45 6.45,2 7,2H8C8.55,2 9,2.45 9,3V5.5H8V2.5C8,2.21 7.8,2 7.5,2M15,2C15.55,2 16,2.45 16,3V5.5H15V2.5C15,2.21 14.8,2 14.5,2C14.2,2 14,2.21 14,2.5V5.5H13V3C13,2.45 13.45,2 14,2H15M17,2C17.55,2 18,2.45 18,3V5.5H17V2.5C17,2.21 16.8,2 16.5,2C16.2,2 16,2.21 16,2.5V5.5H15V3C15,2.45 15.45,2 16,2H17Z" />
                    </svg>
                  </div>
                  <h3>Employee Assistant</h3>
                  <p>Train your AI on product manuals, FAQs, and support protocols—let it handle routine inquiries while you focus on growth.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"Can you explain the return policy for product X?"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H16L12,22L8,18H4A2,2 0 0,1 2,16V4A2,2 0 0,1 4,2M4,4V16H8.83L12,19.17L15.17,16H20V4H4M6,7H18V9H6V7M6,11H16V13H6V11Z" />
                    </svg>
                  </div>
                  <h3>Proposal Generator</h3>
                  <p>Upload past proposals, client feedback, and industry research—create highly customized proposals in a fraction of the time.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"Draft a proposal based on our successful Smith project"</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`tab-panel ${activeTab === 'education' ? 'active' : ''}`}>
              <div className="use-cases-grid">
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20,17A2,2 0 0,0 22,15V4A2,2 0 0,0 20,2H9.46C9.81,2.61 10,3.3 10,4H20V15H11V17M15,7V9H9V22H7V16H5V22H3V14H1.5V9A2,2 0 0,1 3.5,7H15M8,4A2,2 0 0,1 6,6A2,2 0 0,1 4,4A2,2 0 0,1 6,2A2,2 0 0,1 8,4Z" />
                    </svg>
                  </div>
                  <h3>Teacher's Assistant</h3>
                  <p>Upload course materials, lecture notes, and past assignments—create personalized learning resources for individual students.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"Generate a practice worksheet for students struggling with photosynthesis"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z" />
                    </svg>
                  </div>
                  <h3>Student Study Partner</h3>
                  <p>Upload lecture recordings, textbooks, and your notes—create a personalized tutor that knows exactly what you're learning.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"Explain this concept again using a different approach"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13,4A4,4 0 0,1 17,8A4,4 0 0,1 13,12H11V18H9V4H13M13,10A2,2 0 0,0 15,8A2,2 0 0,0 13,6H11V10H13Z" />
                    </svg>
                  </div>
                  <h3>Research Assistant</h3>
                  <p>Upload academic papers, research data, and annotations—organize your findings and generate connections between sources.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"Find contradicting claims in these papers about climate effects"</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`tab-panel ${activeTab === 'health' ? 'active' : ''}`}>
              <div className="use-cases-grid">
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.5,12C18,12 20,14 20,16.5C20,17.38 19.75,18.21 19.31,18.9L22.39,22L21,23.39L17.88,20.32C17.19,20.75 16.37,21 15.5,21C13,21 11,19 11,16.5C11,14 13,12 15.5,12M15.5,14A2.5,2.5 0 0,1 13,16.5A2.5,2.5 0 0,1 15.5,19A2.5,2.5 0 0,1 18,16.5A2.5,2.5 0 0,1 15.5,14M7,15V17H9C9.14,18.55 9.8,19.94 10.81,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3H19A2,2 0 0,1 21,5V13.03C19.85,11.21 17.82,10 15.5,10C14.23,10 13.04,10.37 12.04,11H7V13H10C9.64,13.6 9.34,14.28 9.17,15H7M7,7H17V9H7V7Z" />
                    </svg>
                  </div>
                  <h3>Personal Trainer</h3>
                  <p>Upload workout plans, progress photos, and nutrition logs—get personalized fitness advice based on your specific journey.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"How should I adjust my routine after this knee strain?"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18,13.5A1.5,1.5 0 0,1 19.5,15A1.5,1.5 0 0,1 18,16.5A1.5,1.5 0 0,1 16.5,15A1.5,1.5 0 0,1 18,13.5M6,13.5A1.5,1.5 0 0,1 7.5,15A1.5,1.5 0 0,1 6,16.5A1.5,1.5 0 0,1 4.5,15A1.5,1.5 0 0,1 6,13.5M12,7A1.5,1.5 0 0,1 13.5,8.5A1.5,1.5 0 0,1 12,10A1.5,1.5 0 0,1 10.5,8.5A1.5,1.5 0 0,1 12,7M12,2C13.75,2 15.5,2.38 17.11,3.13C18.73,3.88 20.07,4.94 21.15,6.22L12,22L2.85,6.22C3.93,4.94 5.27,3.88 6.89,3.13C8.5,2.38 10.25,2 12,2Z" />
                    </svg>
                  </div>
                  <h3>Running Coach</h3>
                  <p>Upload GPS data, training logs, and performance metrics—get customized training plans that adapt to your progress and goals.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"Based on my recent runs, am I ready for a half marathon?"</p>
                  </div>
                </div>
                
                <div className="use-case-card">
                  <div className="use-case-visual">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15,11L14,13H12.5L13.5,11H12V8H15M11,11L10,13H8.5L9.5,11H8V8H11M11.5,2A2.5,2.5 0 0,1 14,4.5V5H17A2,2 0 0,1 19,7V19A2,2 0 0,1 17,21H7A2,2 0 0,1 5,19V7A2,2 0 0,1 7,5H10V4.5A2.5,2.5 0 0,1 12.5,2H11.5Z" />
                    </svg>
                  </div>
                  <h3>Nutrition Planner</h3>
                  <p>Upload food logs, nutritional preferences, and dietary restrictions—get meal plans and shopping lists tailored to your health goals.</p>
                  <div className="use-case-example">
                    <span className="example-tag">Example</span>
                    <p>"What high-protein alternatives fit my dietary preferences?"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials">
        <div className="section-header">
          <h2>Real People, <span className="highlight">Real Help</span></h2>
          <p className="section-subtitle">See how others are using Advantis AI to simplify their work and life</p>
        </div>
        
        <div className="testimonials-slider">
          <div className="testimonials-track">
            <div className="testimonial-card">
              <div className="quote-mark">"</div>
              <p className="testimonial-quote">I uploaded my craft guides and audio tips—now my AI helps customers while I focus on creating!</p>
              <div className="testimonial-author">
                <div className="author-avatar">JD</div>
                <div className="author-info">
                  <h4>Jen</h4>
                  <p>Craft Store Owner</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="quote-mark">"</div>
              <p className="testimonial-quote">I added years of notes and podcast clips—it's like having a friend who knows all my best ideas.</p>
              <div className="testimonial-author">
                <div className="author-avatar">AW</div>
                <div className="author-info">
                  <h4>Alex</h4>
                  <p>Writer</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="quote-mark">"</div>
              <p className="testimonial-quote">I uploaded all my course materials and lecture recordings. Now my students have 24/7 access to a tutor that knows exactly what we've covered.</p>
              <div className="testimonial-author">
                <div className="author-avatar">RT</div>
                <div className="author-info">
                  <h4>Robert</h4>
                  <p>University Professor</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="quote-mark">"</div>
              <p className="testimonial-quote">My recipe collection is massive. Now I can ask my AI for dinner ideas based on what's in my fridge—it even suggests adaptations to my favorite dishes!</p>
              <div className="testimonial-author">
                <div className="author-avatar">ML</div>
                <div className="author-info">
                  <h4>Maria</h4>
                  <p>Home Chef</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="quote-mark">"</div>
              <p className="testimonial-quote">I've built an AI that understands my design aesthetic. I feed it my inspiration and sketches, and it helps me articulate concepts I struggled to explain.</p>
              <div className="testimonial-author">
                <div className="author-avatar">JB</div>
                <div className="author-info">
                  <h4>Jordan</h4>
                  <p>Interior Designer</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="slider-controls">
            <button className="slider-dot active" aria-label="Testimonial page 1"></button>
            <button className="slider-dot" aria-label="Testimonial page 2"></button>
            <button className="slider-dot" aria-label="Testimonial page 3"></button>
          </div>
        </div>
      </section>

      {/* Technical Section */}
      <section id="technical" className="technical-section">
        <div className="section-header">
          <h2>Beyond Basic <span className="highlight">RAG</span></h2>
          <p className="section-subtitle">Our platform uses advanced technical architecture to deliver truly personalized AI experiences</p>
        </div>
        
        <div className="technical-grid">
          <div className="technical-card">
            <div className="tech-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3C7.03 3 3 7.03 3 12S7.03 21 12 21C14.2091 21 16.2091 20.1046 17.6569 18.6569C19.1046 17.2091 20 15.2091 20 13H18C18 14.6569 17.3284 16.1569 16.2426 17.2426C15.1569 18.3284 13.6569 19 12 19C8.13 19 5 15.87 5 12S8.13 5 12 5C14.1 5 16.1 5.9 17.5 7.5L15 10H21V4L19.1 5.9C17.2 4.1 14.8 3 12 3M15.5 14.5L14.1 13.1L11 16.2L9.9 15.1L8.5 16.5L11 19L15.5 14.5Z" />
              </svg>
            </div>
            <h3>Context Boundaries</h3>
            <p>Our system doesn't just index your content—it creates defined knowledge boundaries that your AI operates within, ensuring responses are grounded in your specific data.</p>
          </div>
          
          <div className="technical-card">
            <div className="tech-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5M17.13,17C15.92,18.85 14.11,20.24 12,20.92C9.89,20.24 8.08,18.85 6.87,17C6.53,16.5 6.24,16 6,15.47C6,13.82 8.71,12.47 12,12.47C15.29,12.47 18,13.79 18,15.47C17.76,16 17.47,16.5 17.13,17Z" />
              </svg>
            </div>
            <h3>Anti-Hallucination Layers</h3>
            <p>Our proprietary algorithm identifies and filters out responses that aren't directly supported by your data, dramatically reducing the "hallucinations" common in other AI systems. We create a controlled boundary where AI responses are primarily constructed from your content.</p>
          </div>
          
          <div className="technical-card">
            <div className="tech-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,9A1.5,1.5 0 0,1 19,10.5A1.5,1.5 0 0,1 17.5,12M14.5,8A1.5,1.5 0 0,1 13,6.5A1.5,1.5 0 0,1 14.5,5A1.5,1.5 0 0,1 16,6.5A1.5,1.5 0 0,1 14.5,8M9.5,8A1.5,1.5 0 0,1 8,6.5A1.5,1.5 0 0,1 9.5,5A1.5,1.5 0 0,1 11,6.5A1.5,1.5 0 0,1 9.5,8M6.5,12A1.5,1.5 0 0,1 5,10.5A1.5,1.5 0 0,1 6.5,9A1.5,1.5 0 0,1 8,10.5A1.5,1.5 0 0,1 6.5,12M12,3A9,9 0 0,0 3,12A9,9 0 0,0 12,21A9,9 0 0,0 21,12A9,9 0 0,0 12,3Z" />
              </svg>
            </div>
            <h3>Data-Specific Language Processing</h3>
            <p>Unlike generic AI, our system learns the specific language patterns and terminology in your content, understanding your unique expressions and industry-specific terms. The result is an assistant that doesn't just know your information—it embodies your voice and perspective.</p>
          </div>
        </div>
        
        <div className="technical-summary">
          <div className="tech-visual">
            <div className="tech-diagram">
              <div className="diagram-layer" data-layer="Your Data">
                <span>Your Documents, Audio & Data</span>
              </div>
              <div className="diagram-layer" data-layer="Processing">
                <span>Content Processing & Boundary Definition</span>
              </div>
              <div className="diagram-layer" data-layer="Anti-Hallucination">
                <span>Anti-Hallucination Filters</span>
              </div>
              <div className="diagram-layer" data-layer="Response">
                <span>AI Response Generation</span>
              </div>
            </div>
          </div>
          <div className="tech-explanation">
            <h3>It's Your AI, <span className="highlight">Truly</span></h3>
            <p>While other platforms simply retrieve information from your documents, our system adds multiple layers of refinement that deeply personalize the AI to your specific content, voice, and needs. The result is an assistant that doesn't just know your information—it embodies your unique perspective.</p>
            <ul className="tech-benefits">
              <li>Responses built primarily from your content</li>
              <li>Clear source tracing for complete transparency</li>
              <li>Minimal reliance on general AI knowledge</li>
              <li>Language and terminology that matches yours</li>
              <li>Controlled information flow from trusted sources</li>
              <li>Accuracy guarantees through content validation</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Make Life Easier?</h2>
          <p>Upload a ton of files—even audio—and build an AI that's all about you, starting now.</p>
          <Link to="/signup" className="primary-btn cta-button">Start Building Now</Link>
        </div>
        <div className="cta-visual">
          <div className="floating-files">
            <div className="floating-file"></div>
            <div className="floating-file"></div>
            <div className="floating-file"></div>
          </div>
        </div>
      </section>

      {/* For Business */}
      <section id="for-business" className="for-business">
        <div className="section-header">
          <h2>Enterprise <span className="highlight">Solutions</span></h2>
          <p className="section-subtitle">Powerful AI integration for organizations of any size</p>
        </div>
        
        <div className="enterprise-grid">
          <div className="enterprise-card">
            <div className="enterprise-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
              </svg>
            </div>
            <h3>Team Collaboration</h3>
            <p>Create shared AI assistants for your entire team with granular permission controls and collaborative training.</p>
            <ul className="enterprise-features">
              <li>Role-based access controls</li>
              <li>Team training dashboards</li>
              <li>Usage analytics and insights</li>
            </ul>
          </div>
          
          <div className="enterprise-card">
            <div className="enterprise-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M6,16.5L3,19.44V11H6M11,14.66L9.43,13.32L8,14.64V7H11M16,13L13,16V3H16M18.81,12.81L17,11H22V16L20.21,14.21L13,21.36L9.53,18.34L5.75,22H3L9.47,15.66L13,18.64" />
              </svg>
            </div>
            <h3>Live Data Integration</h3>
            <p>Connect your AI to real-time business data sources with automated syncing and continuous learning capabilities.</p>
            <ul className="enterprise-features">
              <li>API integrations with major platforms</li>
              <li>Scheduled data refreshes</li>
              <li>Custom connector development</li>
            </ul>
          </div>
          
          <div className="enterprise-card">
            <div className="enterprise-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16V8C20.53,8.75 21.5,10.3 21.5,12Z" />
              </svg>
            </div>
            <h3>Omnichannel Deployment</h3>
            <p>Deploy your custom AI assistants across multiple platforms including websites, customer portals, and internal tools.</p>
            <ul className="enterprise-features">
              <li>Website widget integration</li>
              <li>Embeddable iFrames</li>
              <li>Custom API endpoints</li>
              <li>Shareable direct links</li>
            </ul>
          </div>
          
          <div className="enterprise-card">
            <div className="enterprise-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M3,3H21V5H3V3M3,7H15V9H3V7M3,11H21V13H3V11M3,15H15V17H3V15M3,19H21V21H3V19Z" />
              </svg>
            </div>
            <h3>Advanced Use Cases</h3>
            <p>Leverage our enterprise platform for specialized business needs with dedicated support and custom development.</p>
            <ul className="enterprise-features">
              <li>Legal compliance assistance</li>
              <li>Internal knowledge management</li>
              <li>Product upselling bots</li>
              <li>Customer service automation</li>
            </ul>
          </div>
          
          <div className="enterprise-card">
            <div className="enterprise-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M21,11C21,16.55 17.16,21.74 12,23C6.84,21.74 3,16.55 3,11V5L12,1L21,5V11M12,21C15.75,20 19,15.54 19,11.22V6.3L12,3.18L5,6.3V11.22C5,15.54 8.25,20 12,21M16.28,14L15.5,13.3C15.11,14 14.31,14.5 13.5,14.5A2.5,2.5 0 0,1 11,12A2.5,2.5 0 0,1 13.5,9.5C14.71,9.5 15.72,10.42 15.96,11.61L16.74,10.95C16.38,9.61 15.05,8.5 13.5,8.5A3.5,3.5 0 0,0 10,12A3.5,3.5 0 0,0 13.5,15.5C14.79,15.5 15.93,14.66 16.28,14M12.75,14H14.25V14.75H14L13.75,16L13,15.75L12.75,14Z" />
              </svg>
            </div>
            <h3>Data Privacy & Compliance</h3>
            <p>Enterprise-grade security with advanced data protection features and comprehensive compliance tooling.</p>
            <ul className="enterprise-features">
              <li>GDPR, HIPAA, and SOC 2 compliance</li>
              <li>End-to-end encryption</li>
              <li>Data residency options</li>
              <li>Audit logging and reporting</li>
            </ul>
          </div>
          
          <div className="enterprise-card">
            <div className="enterprise-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17,3A2,2 0 0,1 19,5V15A2,2 0 0,1 17,17H13V19H14A1,1 0 0,1 15,20H22V22H15A1,1 0 0,1 14,23H10A1,1 0 0,1 9,22H2V20H9A1,1 0 0,1 10,19H11V17H7C5.89,17 5,16.1 5,15V5A2,2 0 0,1 7,3H17M12,14.5H15.5V13H12V14.5M12,12H15.5V10.5H12V12M12,9.5H15.5V8H12V9.5M7,8V9.5H11V8H7M7,10.5V12H11V10.5H7M7,13V14.5H11V13H7Z" />
              </svg>
            </div>
            <h3>Custom Training & Support</h3>
            <p>Dedicated implementation and ongoing support for enterprise deployments with personalized training programs.</p>
            <ul className="enterprise-features">
              <li>White-glove implementation</li>
              <li>Dedicated account management</li>
              <li>Custom training workshops</li>
              <li>24/7 premium support</li>
            </ul>
          </div>
        </div>
        
        <div className="enterprise-cta">
          <h3>Ready to transform your business with custom AI?</h3>
          <p>Our enterprise team will work with you to build the perfect solution for your organization's unique needs.</p>
          <a href="mailto:info@advantissolutions.com" className="secondary-btn">Contact Our Enterprise Team</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-top">
          <div className="footer-logo">Advantis AI</div>
          <div className="footer-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#use-cases">Use Cases</a>
            <a href="#testimonials">Testimonials</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Advantis Solutions LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;